import { ThemeEvents, CartAddEvent, VariantUpdateEvent } from '@theme/events';
import { addItems } from '@theme/mb-cart';

/**
 * "Complete the set": adds the bell and the chosen chain in one request.
 *
 * Deliberately NOT built on `product-form-component`. That component declares
 * `requiredRefs = ['variantId', 'liveRegion']` and then does
 * `const id = formData.get('id'); if (!id) throw new Error('Form ID is required')` - with an
 * `items[]` payload there is no `id`, so it throws. The shape below is copied from it instead:
 * collect the cart section ids, POST, dispatch CartAddEvent with the returned sections so the
 * drawer opens and re-renders itself.
 */
class MbProductSet extends HTMLElement {
  connectedCallback() {
    this.#updateTotals();
    this.addEventListener('change', this.#updateTotals);
    this.querySelector('[data-mb-set-add]')?.addEventListener('click', this.#addSet);
    this.querySelector('[data-mb-set-show-more]')?.addEventListener('click', this.#showMore);
    this.closest('.shopify-section, dialog')?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
  }

  disconnectedCallback() {
    this.closest('.shopify-section, dialog')?.removeEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
  }

  /**
   * Two bells carry a colour option, so the bell's variant id and price are not constant.
   * The new values are read out of the re-rendered markup rather than out of the event
   * payload, so there is one source of truth and it is the one Liquid produced.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #onVariantUpdate = (event) => {
    const updated = event.detail.data.html.querySelector('mb-product-set');
    if (!updated) return;
    this.dataset.bellVariant = updated.dataset.bellVariant;
    this.dataset.bellPrice = updated.dataset.bellPrice;
    this.#updateTotals();
  };

  /**
   * Reveals the rest of the chains IN PLACE. They are already in the document, so this costs
   * no request and never takes the customer off the panel they are configuring. Only once the
   * whole list is open does the link to the full collection make sense, so it appears here.
   */
  #showMore = () => {
    this.querySelectorAll('[data-mb-set-overflow]').forEach((el) => {
      if (el instanceof HTMLElement) el.hidden = false;
    });
    const trigger = this.querySelector('[data-mb-set-show-more]');
    if (trigger instanceof HTMLElement) trigger.hidden = true;
    const viewAll = this.querySelector('[data-mb-set-view-all]');
    if (viewAll instanceof HTMLElement) viewAll.hidden = false;
  };

  /** Timer for the transient "Pridano" label, so a second add restarts it rather than racing. */
  #addedTimer = 0;

  get #checked() {
    return [...this.querySelectorAll('[data-mb-set-item]')].filter(
      (el) => el instanceof HTMLInputElement && el.checked
    );
  }

  /**
   * Formats an amount the way the storefront does, using the platform instead of a regex.
   *
   * This used to take a server-rendered sample ("1 235 Kc"), find the digits in it with a
   * regular expression and splice a new number into the hole. It worked, but it inferred the
   * format from a string - so a currency whose symbol contains a digit, or a format change in
   * the admin, would have produced silent nonsense.
   *
   * `Intl.NumberFormat` knows all of this already. The currency comes from Shopify's own
   * global, and the fraction digits mirror `snippets/cart-money.liquid`, which uses
   * `money_without_trailing_zeros`: a whole amount gets none, real cents get BOTH digits.
   * Leaving `minimumFractionDigits` at 0 throughout would render 28,80 EUR as "28,8".
   */
  #format(cents) {
    const currency = this.dataset.currency || window.Shopify?.currency?.active;
    const locale = document.documentElement.lang || 'cs';
    const hasCents = cents % 100 !== 0;
    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        minimumFractionDigits: hasCents ? 2 : 0,
        maximumFractionDigits: 2,
      }).format(cents / 100);
    } catch {
      // Only if the currency code is missing or invalid, which would mean the page is broken
      // in bigger ways. The number alone beats throwing inside a price update.
      return String(Math.round(cents / 100));
    }
  }

  #updateTotals = () => {
    const checked = this.#checked;
    const base =
      (Number(this.dataset.bellPrice) || 0) +
      checked.reduce((sum, el) => sum + (Number(el.dataset.price) || 0), 0);

    // The reduction is unlocked by a specific product, not by spending more: a bell with its
    // chain is the ordinary purchase, and a 25 Kc pouch does not earn a discount on a 600 Kc
    // order. Without a trigger ticked the panel simply shows the total.
    const unlocked = checked.some((el) => el.hasAttribute('data-mb-set-unlocks-discount'));
    const percent = unlocked ? Number(this.dataset.percent) || 0 : 0;

    // WHICH WAY ROUND THE OFFER RUNS, and why it is this way.
    //
    // Measured against this storefront on 2026-08-11 with a real cart: Shopify reads "customer
    // gets: quantity N" as a MINIMUM and a CAP at once. One eligible item with N = 2 produced no
    // discount at all; three eligible items produced 5% of the two CHEAPEST and left the dearest
    // at full price. So "a percentage off everything, however much is in the basket" cannot be
    // expressed - there is no N that means "any number".
    //
    // Turning the offer around removes the problem instead of working around it: buying a bell or
    // a chain QUALIFIES (the X, where the quantity is only a minimum, so any number works), and
    // the single plush rabbit is what gets cheaper (the Y, quantity 1, always exactly met).
    const triggerPrices = checked
      .filter((el) => el.hasAttribute('data-mb-set-unlocks-discount'))
      .map((el) => Number(el.dataset.price) || 0)
      .sort((a, b) => a - b);
    const qualifies =
      this.dataset.baseQualifies === 'true' || checked.some((el) => el.hasAttribute('data-mb-set-qualifies'));

    const qty = Number(this.dataset.discountQty) || 0;
    const discountable =
      qualifies && qty > 0 && triggerPrices.length >= qty
        ? triggerPrices.slice(0, qty).reduce((sum, p) => sum + p, 0)
        : 0;
    const saving = Math.round((discountable * percent) / 100);

    // THE ROW ITSELF SHOWS THE REDUCTION. A total that quietly drops is easy to miss and easy to
    // distrust; the price on the rabbit's own row crossing out is the offer stated where the
    // decision is made. Only the row that is actually reduced changes, so nothing else on the
    // panel starts looking discounted.
    for (const row of this.querySelectorAll('.mb-product-set__option')) {
      const input = row.querySelector('input[data-mb-set-item]');
      const wasEl = row.querySelector('[data-mb-set-row-was]');
      const nowEl = row.querySelector('[data-mb-set-row-now]');
      if (!wasEl || !nowEl) continue;
      const isReduced =
        saving > 0 && input instanceof HTMLInputElement && input.checked && input.hasAttribute('data-mb-set-unlocks-discount');
      if (isReduced) {
        const full = Number(input.dataset.price) || 0;
        wasEl.textContent = this.#format(full);
        wasEl.hidden = false;
        nowEl.textContent = this.#format(full - Math.round((full * percent) / 100));
      } else {
        wasEl.hidden = true;
        if (wasEl.dataset.restore !== 'done' && input instanceof HTMLInputElement) {
          nowEl.textContent = this.#format(Number(input.dataset.price) || 0);
        }
      }
    }

    const was = this.querySelector('[data-mb-set-was]');
    const now = this.querySelector('[data-mb-set-now]');
    const savingEl = this.querySelector('[data-mb-set-saving]');
    const button = this.querySelector('[data-mb-set-add]');

    if (was) {
      was.textContent = this.#format(base);
      was.hidden = saving <= 0;
    }
    if (now) now.textContent = this.#format(base - saving);
    // Remembered so the post-add verification can compare like with like.
    this.dataset.lastSaving = String(saving);

    // Once the cart has refused a discount, stop offering it again on this page view.
    if (this.dataset.discountUnverified === 'true') {
      if (was) was.hidden = true;
      if (savingEl) savingEl.hidden = true;
      if (now) now.textContent = this.#format(base);
      if (button instanceof HTMLButtonElement) button.textContent = button.dataset.label || '';
      return;
    }

    if (savingEl?.dataset.template) {
      savingEl.textContent = savingEl.dataset.template.replace('{{amount}}', this.#format(saving));
      savingEl.hidden = saving <= 0;
    }

    // The button offers to save only when there is something to save. Promising it otherwise
    // would be the kind of claim §8 of the CRO plan exists to prevent.
    if (button instanceof HTMLButtonElement) {
      // Three states, because the button has to say what it will actually add. Someone who
      // already owns a chain unticks everything and still gets a working button.
      if (saving > 0 && button.dataset.labelSaving) {
        button.textContent = button.dataset.labelSaving.replace('{{amount}}', this.#format(saving));
      } else if (checked.length === 0 && button.dataset.labelAlone) {
        button.textContent = button.dataset.labelAlone;
      } else {
        button.textContent = button.dataset.label || button.textContent;
      }
      button.disabled = false;
    }
  };

  #addSet = async () => {
    const button = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-mb-set-add]'));
    const status = /** @type {HTMLElement | null} */ (this.querySelector('[data-mb-set-status]'));
    const checked = this.#checked;
    if (!button || button.disabled) return;
    let added = false;

    button.disabled = true;

    // No line item properties. Shopify merges cart lines only when variant AND properties
    // match, so a marker here would split "the same chain, bought twice" into two lines of one.
    const items = [
      { id: this.dataset.bellVariant || '' },
      ...checked.map((el) => ({ id: el.dataset.variant || '' })),
    ];

    try {
      const result = await addItems(items);
      if (!result.ok) {
        if (status) status.textContent = result.message || '';
        return;
      }

      const promised = Number(this.dataset.lastSaving) || 0;
      await this.#applyDiscount(promised);

      this.dispatchEvent(
        new CartAddEvent({}, this.dataset.bellVariant || '', {
          source: 'mb-product-set',
          itemCount: checked.length + 1,
          sections: result.sections,
        })
      );

      // Same confirmation the product form gives: the label says "Pridano" for a moment and
      // then goes back to what the button will do NEXT. Without it the only sign that a
      // several-hundred-crown set went in is the cart badge in the header, which on a phone is
      // off screen while the panel is in view - so people click twice.
      added = true;
    } catch (error) {
      console.error('[mb-product-set] add to set failed', error);
      if (status) status.textContent = status.dataset.error || '';
    } finally {
      button.disabled = false;
      this.#updateTotals();
      if (added && button.dataset.labelAdded) {
        button.textContent = button.dataset.labelAdded;
        clearTimeout(this.#addedTimer);
        this.#addedTimer = setTimeout(() => this.#updateTotals(), 2000);
      }
    }
  };

  /**
   * Applies the discount if there is a code, and ALWAYS verifies what the cart actually got.
   *
   * The panel computes its saving from `discount_percent`, a theme setting, while the real
   * reduction lives in the Shopify admin. Liquid cannot read discount definitions, so those two
   * numbers can drift and nothing on the page would notice - the customer would be told 44 Kc
   * and charged the full amount.
   *
   * The cart is the only place that knows the truth, so it is asked. A mismatch cannot be
   * repaired from here, but it can stop being silent: it is logged with the setting to fix, and
   * the promise is taken off the button so the page stops repeating a claim the cart just
   * refused. With no code configured this still runs - an automatic discount can be missing or
   * expired exactly the same way.
   *
   * @param {number} promised - The saving the panel displayed, in minor units.
   */
  async #applyDiscount(promised) {
    // NOTHING PROMISED, NOTHING APPLIED - and this test has to come first.
    //
    // With a discount code configured, the branch below applies the code and, if the cart
    // reports no reduction, NAVIGATES rather than lose a discount silently. Run before this
    // guard, a plain bell-and-chain set (no trigger ticked, no saving offered) would attach the
    // code anyway, find nothing discounted, and throw the customer onto the cart page mid-flow.
    if (promised <= 0) return;

    const code = this.dataset.discountCode;
    const cartUrl = Theme.routes.cart_url;
    const root = cartUrl.replace(/\/cart\/?$/, '');

    if (code) {
      const discountUrl = `${root}/discount/${encodeURIComponent(code)}`;
      try {
        const response = await fetch(`${discountUrl}?redirect=/cart.js`, {
          headers: { Accept: 'application/json' },
          cache: 'no-store',
        });
        const cart = await response.json();
        if (cart?.total_discount) return;
      } catch {
        /* fall through to the navigation below */
      }
      // A code that did not apply is worth a full page load: never a silent loss of a discount.
      window.location.href = `${discountUrl}?redirect=${encodeURIComponent(cartUrl)}`;
      return;
    }

    try {
      const response = await fetch(`${cartUrl}.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' });
      const cart = await response.json();
      if (!cart?.total_discount) {
        console.error(
          '[mb-product-set] The panel promised a discount the cart did not apply. ' +
            'Check that an automatic discount exists in the Shopify admin and that its ' +
            'percentage matches the block setting "Sleva na set".'
        );
        this.dataset.discountUnverified = 'true';
      }
    } catch {
      /* A cart that cannot be read is not evidence of a missing discount. */
    }
  }
}

if (!customElements.get('mb-product-set')) {
  customElements.define('mb-product-set', MbProductSet);
}
